import React, { Fragment, useEffect, useRef } from 'react';
import { animate, timeline } from 'motion';

import CardPatternAnimator from '../../components/CardV2PatternAnimator';
import CardV2 from '../insightV2CardCategory';
import { SplitText } from '@cyriacbr/react-split-text';

const Insight = ({ url, title, image, author, ItemId }) => {
  const targetRef = useRef();
  const patternAnimation = useRef();
  const variation = Math.floor(Math.random() * 6) + 1;
  const animation = useRef();
  const isMobile = window.matchMedia('(max-width: 1025px)').matches;

  const pattern = (
    <div
      className="card-pattern"
      data-variation={variation}
      data-duration="1"
      data-delay="0.25"
      ref={patternAnimation}
      style={{ opacity: 0 }}
    >
      <div className="card-pattern__container">
        <svg
          className="card-pattern__chevron--w-accent"
          width="189"
          height="146"
          viewBox="0 0 189 146"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className="accent"
            d="M62.9625 108.031L126.596 145.139L188.974 109.378L125.749 71.8489L125.164 72.8338L186.701 109.361L126.601 143.816L64.1091 107.373L64.1562 37.0991L63.011 37.0984L62.9625 108.031Z"
          />
          <path
            className="chevron"
            d="M63.1829 36.4746L0 0V71.912L62.8968 108.229L63.1829 108.395L126.08 72.0777L126.366 71.912V0L63.1829 36.4746ZM125.22 1.98468V71.2507L63.1829 107.071L1.14589 71.2507V1.98468L62.8968 37.6323L63.1829 37.7979L125.22 1.98468Z"
          />
        </svg>

        <svg
          className="card-pattern__chevron"
          width="127"
          height="109"
          viewBox="0 0 127 109"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className="chevron"
            d="M1.14587 71.25L63.1829 107.07L125.22 71.25V1.98535L63.4697 37.6323L63.1829 37.798L1.14587 1.98535V71.25ZM126.366 0V71.9114L63.4697 108.229L63.1829 108.394L0.286804 72.077L0 71.9114V0L63.1829 36.4746L126.366 0Z"
          />
        </svg>

        <svg
          className="card-pattern__panels"
          width="190"
          height="147"
          viewBox="0 0 190 147"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            className="panels"
            d="M127.576 0L0.286804 73.4304L0.570374 73.9215H0V145.507L63.1823 109.034L127.023 146.166L127.313 146.336L189.811 109.311L127.576 72.995V0ZM65.1663 37.3252L126.43 1.98401V71.2487H126.409V72.3933L65.1663 37.3252ZM63.7487 108.037V38.1437L64.0178 37.9878L126.409 73.7134V144.485L63.7487 108.037ZM1.14594 74.2568L62.6022 38.8043V108.045L1.14594 143.523V74.2568ZM127.555 74.3098L187.55 109.318L127.555 144.861V74.3098Z"
          />
        </svg>
      </div>
    </div>
  );

  const handleMouseEnter = e => {
    if (isMobile) return;
    const sequence = [];
    const delayMultiplier = 0.1;
    const lines = e.currentTarget.querySelectorAll('.line-wrapper');

    sequence.push([
      patternAnimation.current,
      {
        opacity: [0, 1]
      },
      { at: 0 },
      { duration: 1 }
    ]);

    sequence.push([
      e.currentTarget.querySelector('.insight-cards-item-v2__overlay'),
      {
        transform: ['skew(-45deg) scale(0, 1)', 'skew(-45deg) scale(2, 1)']
      },
      { duration: 0.5, at: 0 }
    ]);

    lines.forEach((line, index) => {
      sequence.push([
        line,
        {
          transform: ['translate(0, 0)', 'translate(100px, 0)']
        },
        { duration: 0.2, at: index * delayMultiplier }
      ]);
    });

    lines.forEach((line, index) => {
      sequence.push([
        line,
        {
          color: '#fff',
          transform: ['translate(-100px, 0)', 'translate(0, 0)']
        },
        {
          duration: 0.25,
          at: index * delayMultiplier + 0.21,
          color: {
            duration: 0.1,
            at: index * delayMultiplier + 0.21
          }
        }
      ]);
    });

    animation.current.inAnimation();
    timeline(sequence, { duration: 1 });
    animate(
      e.currentTarget.querySelector('img'),
      { transform: 'scale(1.2)' },
      { duration: 1 }
    );
  };

  const handleMouseLeave = e => {
    if (isMobile) return;
    e.currentTarget.querySelectorAll('.line-wrapper').forEach((line, index) => {
      const sequence = [
        [
          line,
          {
            opacity: [1, 0],
            transform: ['translate(0, 0)', 'translate(-100px, 0)']
          },
          { duration: 0.15, at: 0.05 * index }
        ],
        [
          line,
          { color: '#1c1c1c' },
          {
            duration: 0,
            at: 0.17 + 0.05 * index
          }
        ],
        [
          line,
          {
            opacity: [0, 1],
            transform: ['translate(100px, 0)', 'translate(0, 0)']
          },
          { duration: 0.15, at: 0.17 + 0.075 * index }
        ]
      ];
      timeline(sequence);
    });

    animation.current.outAnimation();

    animate(patternAnimation.current, { opacity: 0, delay: 0.05 });

    animate(
      e.currentTarget.querySelector('.insight-cards-item-v2__overlay'),
      { transform: 'skew(-45deg) scale(0, 1)' },
      { duration: 0.5 }
    );

    animate(
      e.currentTarget.querySelector('img'),
      { transform: 'scale(1)' },
      { duration: 1 }
    );
  };

  let fromattedAuthor = author;

  if (author) {
    fromattedAuthor = author.replace(/-/g, '\u2011');
  }

  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [url, title, image, author, ItemId]);

  useEffect(() => {
    animation.current = new CardPatternAnimator(patternAnimation.current);
  }, []);

  return (
    <article
      className="insight-v2__card insight-v2__card--listing insight-v2__card--loading"
      aria-labelledby={`insight-card-${ItemId}`}
      ref={targetRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <picture className="image-v2__wrapper">
        <source media="(max-width:677px)" srcSet={image} />
        <img
          src={image}
          alt={title}
          className="image-v2 image-v2--rounded"
          width="840"
          height="473"
        />
      </picture>
      {pattern}
      <a href={url} aria-label={title}></a>
      <div className="insight-cards-item-v2__overlay"></div>
      <div className="insight-v2-card__content">
        <h1 id={`insight-card-${ItemId}`} className="insight-v2-card__heading">
          <SplitText
            LineWrapper={({ children }) => (
              <span className="line-wrapper">{children}</span>
            )}
          >
            {title}
          </SplitText>
        </h1>
        {author !== '' && (
          <div className="insight-v2-card__author">
            <SplitText
              LineWrapper={({ children }) => (
                <span className="line-wrapper">{children}</span>
              )}
            >
              {fromattedAuthor}
            </SplitText>
          </div>
        )}
      </div>
    </article>
  );
};

Insight.propTypes = {};

export default Insight;
